import { useEffect } from 'react';

interface CertificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const certifications = [
  {
    icon: 'ri-shield-check-line',
    name: 'HACCP',
    fullName: 'Hazard Analysis & Critical Control Points',
    issuer: 'CFIA / Internal Audit Body',
    scope: 'Entire cold-chain operation — transport, warehousing, and distribution of temperature-sensitive food products',
    highlight: 'Core standard',
    color: 'from-[#DC2626] to-[#F97316]',
    desc: 'Our operations are built around a comprehensive HACCP plan that identifies, evaluates, and controls biological, chemical, and physical hazards at every critical control point within the food supply chain.',
  },
  {
    icon: 'ri-medal-line',
    name: 'ISO 22000',
    fullName: 'Food Safety Management System',
    issuer: 'Accredited Certification Body',
    scope: 'Food safety management across all logistics and warehousing facilities',
    highlight: 'International standard',
    color: 'from-[#1A365D] to-[#2563EB]',
    desc: 'ISO 22000 certification demonstrates our ability to identify and control food safety hazards in alignment with global best practices, ensuring consistent food safety performance across all service areas.',
  },
  {
    icon: 'ri-award-line',
    name: 'SQF Level 2',
    fullName: 'Safe Quality Food Program — Level 2',
    issuer: 'SQF Institute (SQFI)',
    scope: 'Cold storage warehousing and distribution operations',
    highlight: 'GFSI recognised',
    color: 'from-[#059669] to-[#10B981]',
    desc: 'Safe Quality Food Level 2 certification confirms that our food safety and quality management systems meet the rigorous standards recognised by the Global Food Safety Initiative (GFSI), a trusted benchmark for major retailers and foodservice operators.',
  },
  {
    icon: 'ri-government-line',
    name: 'CFIA Registered',
    fullName: 'Canadian Food Inspection Agency — Registered Facility',
    issuer: 'Government of Canada — CFIA',
    scope: 'Registered cold storage and handling facilities for federally inspected food products',
    highlight: 'Federal compliance',
    color: 'from-[#7C3AED] to-[#A78BFA]',
    desc: 'Registration with the Canadian Food Inspection Agency confirms that our facilities meet federal regulatory requirements for the safe storage and handling of food products, including those subject to federal inspection programs.',
  },
  {
    icon: 'ri-truck-line',
    name: 'Transport Canada',
    fullName: 'Commercial Vehicle Safety & Compliance',
    issuer: 'Transport Canada / Provincial Authorities',
    scope: 'All commercial vehicle operations across Canada',
    highlight: 'Regulatory compliance',
    color: 'from-[#D97706] to-[#F59E0B]',
    desc: 'Full compliance with Transport Canada regulations, including Hours of Service (HOS), National Safety Code (NSC) standards, and provincial carrier safety ratings — ensuring every vehicle in our fleet operates safely and legally across all jurisdictions.',
  },
  {
    icon: 'ri-leaf-line',
    name: 'LEED Silver',
    fullName: 'Leadership in Energy and Environmental Design',
    issuer: 'Canada Green Building Council (CaGBC)',
    scope: 'Modern logistics and warehousing facilities',
    highlight: 'Green building',
    color: 'from-[#16A34A] to-[#4ADE80]',
    desc: 'LEED Silver certification reflects our commitment to sustainable facility design — encompassing energy-efficient refrigeration systems, optimised insulation, reduced water consumption, and responsible construction and operations practices.',
  },
];

const trustPoints = [
  {
    icon: 'ri-checkbox-circle-line',
    text: 'All certifications are maintained through regular third-party audits and continuous internal monitoring',
  },
  {
    icon: 'ri-eye-line',
    text: 'Full compliance documentation is available to clients and partners upon request',
  },
  {
    icon: 'ri-refresh-line',
    text: 'Certification programmes are reviewed and renewed on a scheduled basis to reflect the latest regulatory requirements',
  },
  {
    icon: 'ri-team-line',
    text: 'Dedicated Food Safety & Compliance team oversees all standards across every operational site',
  },
];

export default function CertificationsModal({ isOpen, onClose }: CertificationsModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative h-52 md:h-64 rounded-t-2xl overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/5a81860ae45bc80ccbd9afda350dac34.jpg"
            alt="McALLISTER DELIGHT Certifications"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <p className="text-[#DC2626] text-xs font-bold uppercase tracking-widest mb-2">
              COMPLIANCE & QUALITY
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Certifications & Standards
            </h2>
          </div>
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-8 md:p-10">
          {/* Intro */}
          <div className="mb-10">
            <p className="text-gray-600 leading-relaxed text-base">
              At McALLISTER DELIGHT, compliance is not a checkbox — it is the foundation of every
              operation. Our facilities and fleet are certified and registered under the most
              widely recognised food safety, transport, and environmental standards in Canada
              and internationally. These certifications give our clients the confidence that
              their products are handled with the highest level of care throughout the entire
              cold chain.
            </p>
          </div>

          {/* Certs grid */}
          <div className="grid md:grid-cols-2 gap-5 mb-10">
            {certifications.map((cert, i) => (
              <div
                key={i}
                className="rounded-xl border border-gray-100 bg-gray-50 p-5 hover:border-gray-200 transition-colors"
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-11 h-11 flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${cert.color} rounded-xl`}>
                    <i className={`${cert.icon} text-white text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-bold text-[#1A365D] text-base">{cert.name}</span>
                      <span className="px-2 py-0.5 rounded-full bg-[#DC2626]/10 text-[#DC2626] text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                        {cert.highlight}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 font-medium mb-3">{cert.fullName}</div>
                    <p className="text-gray-600 text-xs leading-relaxed mb-3">{cert.desc}</p>
                    <div className="space-y-1">
                      <div className="flex items-start gap-2 text-xs text-gray-400">
                        <i className="ri-building-line mt-0.5 flex-shrink-0"></i>
                        <span><span className="font-semibold text-gray-500">Issuer:</span> {cert.issuer}</span>
                      </div>
                      <div className="flex items-start gap-2 text-xs text-gray-400">
                        <i className="ri-focus-3-line mt-0.5 flex-shrink-0"></i>
                        <span><span className="font-semibold text-gray-500">Scope:</span> {cert.scope}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Trust section */}
          <div className="bg-[#1A365D]/4 rounded-2xl p-6 mb-6">
            <h3 className="text-base font-bold text-[#1A365D] mb-4">Our Commitment to Ongoing Compliance</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {trustPoints.map((point, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${point.icon} text-[#DC2626] text-base`}></i>
                  </div>
                  <p className="text-gray-600 text-xs leading-relaxed">{point.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* All certs strip */}
          <div className="border-t border-gray-100 pt-6">
            <p className="text-xs text-gray-400 uppercase font-bold tracking-widest mb-4">
              Active Certifications
            </p>
            <div className="flex flex-wrap gap-3">
              {certifications.map((cert) => (
                <span
                  key={cert.name}
                  className="px-4 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold"
                >
                  {cert.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
